/**
 * @file ProductInformationSection.tsx
 *
 * Core text fields of the product form: name (required), publication status,
 * description, and short description. Mirrors WSB-v2's Gravity form primitives
 * (`gv-form-option` / `gv-label`, native `gv-input-select` / `gv-input-textarea`);
 * the name input carries the validation error and the focus ref the form scrolls
 * to on a failed submit, and both description labels show an "Optional" caption.
 * The short description is two rows — a summary line, not a second long
 * description — and is what the detail drawer previews.
 */

import type { RefObject } from 'react';
import { useId } from 'react';
import { GeneralField, Icon } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import styles from './ProductInformationSection.module.css';

interface ProductInformationSectionProps {
  name: string;
  onNameChange: (value: string) => void;
  nameError?: string;
  nameRef: RefObject<HTMLInputElement | null>;
  status: string;
  onStatusChange: (value: string) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  shortDescription: string;
  onShortDescriptionChange: (value: string) => void;
}

/**
 * Renders the name, status, and description fields.
 *
 * @param props - Field values, change handlers, the name error, and focus ref.
 *
 * @returns The product-information fields.
 */
export function ProductInformationSection({
  name,
  onNameChange,
  nameError,
  nameRef,
  status,
  onStatusChange,
  description,
  onDescriptionChange,
  shortDescription,
  onShortDescriptionChange,
}: ProductInformationSectionProps) {
  const intl = useIntl();
  const nameId = useId();
  const descriptionId = useId();
  const shortDescriptionId = useId();

  return (
    <>
      <div className="gv-flex gv-flex-row gv-gap-md gv-w-full gv-max-tab-flex-col">
        <div className="gv-form-option gv-w-full">
          <label className="gv-label" htmlFor={nameId}>
            <span>
              <FormattedMessage {...adminMessages.formName} />
            </span>
          </label>
          <GeneralField
            id={nameId}
            inputRef={nameRef as RefObject<HTMLInputElement>}
            value={name}
            data-testid="product-form-name"
            placeholder={intl.formatMessage(adminMessages.formNamePlaceholder)}
            error={nameError}
            aria-label={intl.formatMessage(adminMessages.formName)}
            onChange={(event) => onNameChange(event.target.value)}
          />
        </div>

        <div className="gv-form-option gv-w-full">
          <span className="gv-label">
            <FormattedMessage {...adminMessages.formStatus} />
          </span>
          <div className="gv-input gv-input-select gv-w-full">
            <select
              value={status}
              data-testid="product-form-status"
              aria-label={intl.formatMessage(adminMessages.formStatus)}
              onChange={(event) => onStatusChange(event.target.value)}
            >
              <option value="publish">
                {intl.formatMessage(adminMessages.formStatusPublished)}
              </option>
              <option value="draft">
                {intl.formatMessage(adminMessages.formStatusDraft)}
              </option>
              <option value="private">
                {intl.formatMessage(adminMessages.formStatusHidden)}
              </option>
            </select>
            <Icon name="expand_more" size="small" />
          </div>
        </div>
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={descriptionId}>
          <span>
            <FormattedMessage {...adminMessages.formDescription} />
          </span>
          <span className="gv-label-optional">
            <FormattedMessage {...adminMessages.formOptional} />
          </span>
        </label>
        <textarea
          id={descriptionId}
          className={`gv-input gv-input-textarea ${styles.textareaMin} gv-w-full`}
          value={description}
          rows={4}
          data-testid="product-form-description"
          aria-label={intl.formatMessage(adminMessages.formDescription)}
          onChange={(event) => onDescriptionChange(event.target.value)}
        />
      </div>

      <div className="gv-form-option gv-w-full">
        <label className="gv-label" htmlFor={shortDescriptionId}>
          <span>
            <FormattedMessage {...adminMessages.formShortDescription} />
          </span>
          <span className="gv-label-optional">
            <FormattedMessage {...adminMessages.formOptional} />
          </span>
        </label>
        <textarea
          id={shortDescriptionId}
          className={`gv-input gv-input-textarea ${styles.textareaMin} gv-w-full`}
          value={shortDescription}
          rows={2}
          data-testid="product-form-short-description"
          aria-label={intl.formatMessage(adminMessages.formShortDescription)}
          onChange={(event) => onShortDescriptionChange(event.target.value)}
        />
      </div>
    </>
  );
}
